import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { customizeValidator } from "@rjsf/validator-ajv8";
import JSONSchemaForm from "@rjsf/core";
import { useTranslation } from "react-i18next";
import { MouseEventHandler, useContext, useEffect, useState } from "react";
import customFormats from "@beeldit/core/custom-validations/custom-validation";
import useProcessSchema from "@beeldit/core/hooks/useProcessSchema";
import widgets from "@beeldit/core/widgets/widgets";
import PlayerSelector from "@main/players/components/PlayerSelector";
import BeelditSwitchInput from "@beeldit/core/components/BeelditSwitchInput";

interface Prop {
  formConfig: {
    formRef: any;
    onSubmit: any;
    context: any;
    className?: string;
    filterData?: {
      category_id?: number;
    }
  };
}

interface Context {
  element: any;
  setElement: (element: any) => MouseEventHandler;
  backendFormErrors: any;
}

function GameForm(props: Prop) {
  const { formRef, onSubmit, context, className, filterData } = props.formConfig;
  const { element, setElement, backendFormErrors } =
    useContext<Context>(context);

  const { t } = useTranslation();

  const [finalSchema, setFinalSchema] = useState<RJSFSchema>({});

  const translations = {
    code: t("code"),
    day: t("game_day"),
    date: t("date"),
    time: t("time"),
    status: t("status"),
    first_player_id: t("first_player_id"),
    second_player_id: t("second_player_id"),
    first_player_forfeit: t("first_player_forfeit"),
    second_player_forfeit: t("second_player_forfeit"),
    first_player_score: t("first_player_score"),
    second_player_score: t("second_player_score"),
    category_id: t("category_id"),
  };

  let schema: RJSFSchema = {
    type: "object",
    // required: ['minutes', 'customer_id', 'task', 'date', 'report_id'],
    properties: {      
      day: {
        type: "string",
        title: translations.day,
      },
      code: {
        type: "string",
        title: translations.code,
      },
      date: {
        type: "string",
        title: translations.date,
        format: "date",
      },
      time: {
        type: "string",
        title: translations.time,
        format: "time",
      },
      first_player_id: {
        type: "number",
        title: translations.first_player_id,
      },
      second_player_id: {
        type: "number",
        title: translations.second_player_id,
      },
      first_player_forfeit: {
        type: "boolean",
        title: translations.first_player_forfeit,
        default: false,
      },
      second_player_forfeit: {
        type: "boolean",
        title: translations.second_player_forfeit,
        default: false,
      },
      first_player_score: {
        type: "number",
        title: translations.first_player_score,
        default: 0,
        minimum: 0,
        maximum: 6,
      },
      second_player_score: {
        type: "number",
        title: translations.second_player_score,
        default: 0,
        minimum: 0,
        maximum: 6,
      },
      status: {
        type: "number",
        title: translations.status,
        default: 0,
        anyOf: [
          {
            title: "Por jugar",
            const: 0,
          },
          {
            title: "Jugado",
            const: 1,
          },
          {
            title: "Pendiente de revisión",
            const: 2,
          },
          {
            title: "Validado",
            const: 3,
          }
        ],
      },
    },
  };
  const uiSchema: UiSchema = {
    "ui:classNames": className,    
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    "day": {
      "ui:options": {
        placeholder: "J(X)"
      }
    },
    "code": {
      "ui:options": {
        placeholder: "J(X)P(Y)"
      }
    },
    first_player_forfeit: {
      "ui:widget": "switch",
    },
    second_player_forfeit: {
      "ui:widget": "switch",
    },
    first_player_id: {
      "ui:widget": "player-selector",
      "ui:options": {
        filters: {
          category_id: filterData?.category_id,
        }
      }
    },
    second_player_id: {
      "ui:widget": "player-selector",
      "ui:options": {
        filters: {
          category_id: filterData?.category_id,
        }
      }
    },
  };
  const customWidgets = {
    ... {
      "switch": BeelditSwitchInput,
      "player-selector": PlayerSelector,
    },
    ...widgets,
  };
  const handleChange = (type: any) => {
    /** Este método de momento es necesario aunque en realidad no tenga sentido, pero se debe a un bug de la libreria */
    // Actualizar el estado solo si no hay errores de validación
    setElement(type.formData);
    return console.log.bind(console, type);
  };
  const log = (type: any) => {
    return console.log.bind(console, type);
  };

  const formats = customFormats;
  const validator = customizeValidator({ customFormats: formats });

  const processSchemaFunction = useProcessSchema();

  useEffect(() => {
    setFinalSchema(processSchemaFunction(schema, element));
  }, [element]);

  return (
    <JSONSchemaForm
      noHtml5Validate
      showErrorList={false}
      ref={formRef}
      schema={schema}
      uiSchema={uiSchema}
      widgets={customWidgets}
      formData={element}
      validator={validator}
      onChange={handleChange}
      onSubmit={onSubmit}
      onError={log("errors")}
      extraErrors={backendFormErrors}
    />
  );
}

export default GameForm;
